import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LocationPicker from '../components/LocationPicker';
import PHAddressSelect from '../components/PHAddressSelect';
import TermsModal from '../components/TermsModal';
import * as LucideIcons from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRICE_UNITS = [
  { value: 'per hour', label: 'Per Hour' },
  { value: 'per day', label: 'Per Day' },
  { value: 'per job', label: 'Per Job' },
  { value: 'per project', label: 'Per Project' },
  { value: 'fixed', label: 'Fixed Price' }
];

const CURRENCIES = ['PHP', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'JPY'];

// Helper to render Lucide icon by name
const renderIcon = (iconName, className = "w-4 h-4") => {
  if (!iconName) return null;
  const IconComponent = LucideIcons[iconName];
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  return null;
};

const PostService = ({ user, onLogout, onUpdate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0); // 0 = clearance check, 1+ = service form
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [useRegisteredLocation, setUseRegisteredLocation] = useState(true);
  const [customAddr, setCustomAddr] = useState({ province: '', city: '', barangay: '' });
  const [hasClearance, setHasClearance] = useState(!!user?.police_clearance);
  const [uploadingClearance, setUploadingClearance] = useState(false);
  const [clearanceFile, setClearanceFile] = useState(null);
  const [clearancePreview, setClearancePreview] = useState(null);
  const [clearanceRequired, setClearanceRequired] = useState(true);

  // Build user's registered location string
  const getUserRegisteredLocation = () => {
    if (!user) return '';
    const parts = [
      user.street_address,
      user.barangay,
      user.city,
      user.province
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : user.location || '';
  };

  const formatSavedLocation = (loc) => {
    const parts = [loc.street, loc.barangay, loc.city, loc.province].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : loc.full_address || '';
  };

  const getMyLocationOptions = () => {
    const options = [];
    const registered = getUserRegisteredLocation();
    if (registered) options.push({ key: 'registered', label: 'Registered address', address: registered });
    (user?.saved_locations || []).forEach((loc) => {
      const address = formatSavedLocation(loc);
      if (address) options.push({ key: loc.id, label: loc.label || 'Saved location', address });
    });
    return options;
  };
  const [selectedMyLocationKey, setSelectedMyLocationKey] = useState('registered');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    currency: 'PHP',
    price_unit: 'per hour',
    location: '',
    images: [],
    availability: 'available',
    // Additional showcase fields
    highlights: ['', '', ''],
    requirements: '',
    includes: '',
    experience_years: ''
  });

  const [imagePreviews, setImagePreviews] = useState([]);

  useEffect(() => {
    fetchCategories();
    // Check if user has accepted terms
    if (user && !user.terms_accepted) {
      setShowTermsModal(true);
    }
    // Set default location to user's registered location
    if (user) {
      const first = getMyLocationOptions()[0];
      if (first) {
        setSelectedMyLocationKey(first.key);
        setFormData(prev => ({ ...prev, location: first.address }));
      }
    }
    // Fetch platform config for clearance requirement
    const checkClearanceRequirement = async () => {
      try {
        const res = await axios.get(`${API}/platform/config`);
        const required = res.data.require_barangay_clearance !== false;
        setClearanceRequired(required);
        // Skip clearance step if not required OR if already uploaded
        if (!required || (user && user.police_clearance)) {
          setHasClearance(true);
          setStep(1);
        }
      } catch (err) {
        // Default: require clearance, skip if user already has it
        if (user && user.police_clearance) {
          setHasClearance(true);
          setStep(1);
        }
      }
    };
    checkClearanceRequirement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleAcceptTerms = async () => {
    try {
      const response = await axios.post(`${API}/auth/accept-terms`);
      if (onUpdate) {
        onUpdate(response.data);
      }
      setShowTermsModal(false);
    } catch (error) {
      console.error('Error accepting terms:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleClearanceFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File must be less than 5MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Only JPEG, PNG, and WebP images are allowed');
      return;
    }

    setClearanceFile(file);
    setError('');
    const reader = new FileReader();
    reader.onloadend = () => setClearancePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleClearanceUpload = async () => {
    if (!clearanceFile) {
      setError('Please select a Barangay Clearance document first');
      return;
    }

    setUploadingClearance(true);
    setError('');

    try {
      // Step 1: Upload the file
      const uploadData = new FormData();
      uploadData.append('file', clearanceFile);
      uploadData.append('image_type', 'clearance');

      const token = localStorage.getItem('token');
      const uploadRes = await axios.post(`${API}/upload/id-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });

      const clearanceUrl = uploadRes.data.url;

      // Step 2: Save clearance URL to user profile
      await axios.post(`${API}/kyc/barangay-clearance`, { clearance_url: clearanceUrl }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Step 3: Update local state and advance
      setHasClearance(true);
      setStep(1);

      // Refresh the user data if callback exists
      if (onUpdate) {
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        onUpdate(meRes.data);
      }
    } catch (err) {
      console.error('Clearance upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload Barangay Clearance. Please try again.');
    } finally {
      setUploadingClearance(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleHighlightChange = (index, value) => {
    const newHighlights = [...formData.highlights];
    newHighlights[index] = value;
    setFormData({ ...formData, highlights: newHighlights });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (formData.images.length + files.length > 10) {
      setError('Maximum 10 images allowed');
      return;
    }

    setUploadingImages(true);
    setError('');

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each file must be less than 5MB');
        continue;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Only JPEG, PNG, and WebP images are allowed');
        continue;
      }

      // Upload to server FIRST, then add preview only if successful
      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          // Upload successful - now add the image URL and preview
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, response.data.url]
          }));

          // Create preview for display
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(file);
          
          console.log('Image uploaded successfully:', response.data.url);
        } else {
          setError('Upload failed - no URL returned');
        }
      } catch (error) {
        console.error('Image upload error:', error);
        setError(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingImages(false);
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
    setImagePreviews(newPreviews);
  };

  const handleLocationSelect = (selectedLocation) => {
    if (selectedLocation) {
      setFormData({ ...formData, location: selectedLocation.name });
    } else {
      setFormData({ ...formData, location: '' });
    }
  };

  const validateStep = (stepNum) => {
    setError('');
    if (stepNum === 1) {
      if (!formData.title || !formData.category || !formData.description) {
        setError('Please fill in title, category, and description');
        return false;
      }
    } else if (stepNum === 2) {
      if (!formData.price || parseFloat(formData.price) <= 0) {
        setError('Please enter a valid price');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(Math.max(1, step - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep(3)) return;

    setLoading(true);

    try {
      // Build description with additional info
      let fullDescription = formData.description;
      
      const highlights = formData.highlights.filter(h => h.trim());
      if (highlights.length > 0) {
        fullDescription += '\n\n**Key Highlights:**\n' + highlights.map(h => `• ${h}`).join('\n');
      }
      
      if (formData.includes.trim()) {
        fullDescription += '\n\n**What\'s Included:**\n' + formData.includes;
      }
      
      if (formData.requirements.trim()) {
        fullDescription += '\n\n**Requirements:**\n' + formData.requirements;
      }

      const submitData = {
        title: formData.title,
        description: fullDescription,
        category: formData.category,
        price: parseFloat(formData.price),
        currency: formData.currency,
        price_unit: formData.price_unit,
        location: formData.location,
        images: formData.images,
        availability: formData.availability
      };

      const token = localStorage.getItem('token');
      await axios.post(`${API}/services`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'provider' && user?.active_role !== 'provider') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">Only service providers can post services.</p>
          </div>
        </div>
      </div>
    );
  }

  // Block users with rejected ID verification
  if (user?.id_verification_status === 'rejected') {
    const reasons = user?.kyc_rejection_reasons || [];
    const latestReason = reasons.length > 0 ? reasons[reasons.length - 1]?.reason : 'Your submitted documents did not meet our requirements.';
    return (
      <div className="min-h-screen bg-gray-50" data-testid="post-service-rejected">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-lg mx-auto px-4 py-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LucideIcons.ShieldX className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">ID Verification Rejected</h2>
            <p className="text-gray-600 text-sm mb-4">
              Your identity verification has been rejected. You cannot create services until your ID is verified.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm font-semibold text-red-700 mb-1">Rejection Reason:</p>
              <p className="text-sm text-red-600">{latestReason}</p>
            </div>
            <p className="text-gray-500 text-xs mb-4">
              Please re-submit your ID documents from your Account Settings to get verified.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/account-settings')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg"
                data-testid="go-to-settings-btn"
              >
                Go to Account Settings
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-50"
                data-testid="go-to-dashboard-btn"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Block users who are not yet KYC verified
  if (!user?.id_verified) {
    const status = user?.id_verification_status;
    const isPending = status === 'pending' || status === 'pending_admin_review';
    return (
      <div className="min-h-screen bg-gray-50" data-testid="post-service-not-verified">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-lg mx-auto px-4 py-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LucideIcons.ShieldAlert className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {isPending ? 'ID Verification Pending' : 'ID Verification Required'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              {isPending
                ? 'Your ID documents are currently being reviewed. You will be able to create services once your identity is verified.'
                : 'You need to complete ID verification before you can create services. Please submit your documents from your Account Settings.'}
            </p>
            <div className="flex gap-3 justify-center">
              {!isPending && (
                <button
                  onClick={() => navigate('/account-settings')}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg"
                  data-testid="go-to-settings-btn"
                >
                  Submit ID Documents
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-50"
                data-testid="go-to-dashboard-btn"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="post-service-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
            + {t('ux.postAService')}
          </h1>
          <p className="text-gray-600 mt-2">{t('ux.showcaseSkills')}</p>
        </div>

        {/* Progress Steps - only show for steps 1-4 */}
        {step >= 1 && (
        <>
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <p className="text-center text-sm text-gray-600 mb-6">
          {t('ux.stepOf4', { step, label:
            step === 1 ? t('ux.basicInformation')
            : step === 2 ? t('ux.labelPricing')
            : step === 3 ? t('ux.labelGallery')
            : t('ux.labelReview') })}
        </p>
        </>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit}>
            {/* Step 0: Barangay Clearance Upload (pre-step for clients) */}
            {step === 0 && (
              <div data-testid="step-clearance" className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LucideIcons.ShieldCheck className="w-8 h-8 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Barangay Clearance Required</h2>
                  <p className="text-gray-600 text-sm max-w-md mx-auto">
                    Before you can post a service, you need to upload a valid Barangay Clearance certificate. This is a one-time requirement for all service providers.
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition">
                  {clearancePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={clearancePreview}
                        alt="Barangay Clearance Preview"
                        className="max-h-56 mx-auto rounded-lg shadow"
                        data-testid="clearance-preview-img"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setClearanceFile(null);
                          setClearancePreview(null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow hover:bg-red-600"
                        data-testid="clearance-remove-btn"
                      >
                        <LucideIcons.X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <LucideIcons.FileUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-700 font-medium">Click to upload Barangay Clearance</p>
                      <p className="text-xs text-gray-400 mt-1">JPEG, PNG, or WebP (max 5MB)</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleClearanceFileSelect}
                        className="hidden"
                        data-testid="clearance-file-input"
                      />
                    </label>
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                    data-testid="clearance-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleClearanceUpload}
                    disabled={!clearanceFile || uploadingClearance}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="clearance-submit-btn"
                  >
                    {uploadingClearance ? 'Uploading...' : 'Submit & Continue'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div data-testid="step-1" className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    {t('ux.serviceTitle')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={t('ux.serviceTitlePlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="100"
                    data-testid="service-title-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.title.length}/100</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    {t('ux.category')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-category-select"
                  >
                    <option value="">{t('ux.selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    {t('ux.description')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder={t('ux.descPlaceholder')}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-description-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    {t('ux.keyHighlights')}
                  </label>
                  {formData.highlights.map((highlight, index) => (
                    <input
                      key={index}
                      type="text"
                      value={highlight}
                      onChange={(e) => handleHighlightChange(index, e.target.value)}
                      placeholder={t(`ux.highlight${index + 1}`)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    />
                  ))}
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                    data-testid="cancel-btn"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold"
                    data-testid="next-step-btn"
                  >
                    {t('ux.nextPricing')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Pricing & Location */}
            {step === 2 && (
              <div data-testid="step-2" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-gray-700 font-bold mb-2">
                      Price <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="service-price-input"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">Currency</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2">Price Unit</label>
                    <select
                      name="price_unit"
                      value={formData.price_unit}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {PRICE_UNITS.map((unit) => (
                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">Service Location</label>
                  
                  {/* Option to use registered/saved location */}
                  {getMyLocationOptions().length > 0 && (
                    <div className="mb-3">
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="location-option-registered">
                        <input
                          type="radio"
                          name="locationOption"
                          checked={useRegisteredLocation}
                          onChange={() => {
                            setUseRegisteredLocation(true);
                            const opts = getMyLocationOptions();
                            const sel = opts.find(o => o.key === selectedMyLocationKey) || opts[0];
                            setSelectedMyLocationKey(sel.key);
                            setFormData({ ...formData, location: sel.address });
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800">Use my registered location</p>
                          <p className="text-sm text-gray-500">Registered address or a saved location</p>
                        </div>
                      </label>
                      {useRegisteredLocation && (
                        <select
                          value={selectedMyLocationKey}
                          onChange={(e) => {
                            const sel = getMyLocationOptions().find(o => o.key === e.target.value);
                            if (!sel) return;
                            setSelectedMyLocationKey(sel.key);
                            setFormData({ ...formData, location: sel.address });
                          }}
                          className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="my-location-select"
                        >
                          {getMyLocationOptions().map(o => (
                            <option key={o.key} value={o.key}>{o.label} — {o.address}</option>
                          ))}
                        </select>
                      )}
                      
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 mt-2" data-testid="location-option-custom">
                        <input
                          type="radio"
                          name="locationOption"
                          checked={!useRegisteredLocation}
                          onChange={() => {
                            setUseRegisteredLocation(false);
                            setFormData({ ...formData, location: '' });
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <p className="font-medium text-gray-800">Use a different location</p>
                          <p className="text-sm text-gray-500">Search or pick from map</p>
                        </div>
                      </label>
                    </div>
                  )}
                  
                  {/* Show location picker if using different location or no registered location */}
                  {(!useRegisteredLocation || getMyLocationOptions().length === 0) && (
                    <div className="space-y-3">
                      <PHAddressSelect
                        province={customAddr.province}
                        city={customAddr.city}
                        barangay={customAddr.barangay}
                        onChange={(next) => {
                          setCustomAddr(next);
                          const composed = [next.barangay, next.city, next.province].filter(Boolean).join(', ');
                          setFormData((prev) => ({ ...prev, location: composed }));
                        }}
                        idPrefix="service-location"
                      />
                      <div className="text-xs text-gray-400 text-center">— or pinpoint the exact spot on the map —</div>
                      <LocationPicker
                        onLocationSelect={handleLocationSelect}
                        showRadius={false}
                        placeholder="Select where you provide this service..."
                      />
                    </div>
                  )}
                  
                  {formData.location && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm text-green-700 font-medium">{formData.location}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">What's Included</label>
                  <textarea
                    name="includes"
                    value={formData.includes}
                    onChange={handleChange}
                    placeholder="List what clients will receive (e.g., consultation, revisions, delivery time)"
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2">Requirements from Client</label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="What do you need from the client to start? (e.g., project details, materials, access)"
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold"
                    data-testid="next-step-btn"
                  >
                    Next: Gallery →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Gallery & Media */}
            {step === 3 && (
              <div data-testid="step-3" className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    Service Images (up to 10)
                  </label>
                  <p className="text-sm text-gray-500 mb-4">
                    Add photos that showcase your work. First image will be the main display.
                  </p>

                  {/* Image Preview Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={preview} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        >
                          ×
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                            Main
                          </span>
                        )}
                      </div>
                    ))}

                    {formData.images.length < 10 && (
                      <label className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImages}
                        />
                        {uploadingImages ? (
                          <span className="text-gray-500">Uploading...</span>
                        ) : (
                          <>
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-sm text-gray-500 mt-1">Add Photo</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold"
                    data-testid="next-step-btn"
                  >
                    Next: Review →
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Review & Publish */}
            {step === 4 && (
              <div data-testid="step-4" className="space-y-6">
                <h3 className="text-xl font-bold">Review Your Service Listing</h3>

                {/* Preview Card */}
                <div className="border rounded-lg overflow-hidden">
                  {imagePreviews.length > 0 ? (
                    <img
                      src={imagePreviews[0]}
                      alt="Service preview"
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-500">
                      No image uploaded
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {formData.category || 'Category'}
                    </span>
                    <h4 className="text-xl font-bold mt-2">{formData.title || 'Service Title'}</h4>
                    <p className="text-gray-600 mt-2 line-clamp-3">
                      {formData.description || 'Service description will appear here'}
                    </p>
                    <div className="flex justify-between items-center mt-4">
                      <span className="text-2xl font-bold text-blue-600">
                        {formData.currency} {formData.price || '0'} <span className="text-sm text-gray-500">{formData.price_unit}</span>
                      </span>
                      {formData.location && (
                        <span className="text-gray-500 text-sm">📍 {formData.location}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Highlights Preview */}
                {formData.highlights.filter(h => h.trim()).length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold mb-2">Key Highlights</h4>
                    <ul className="list-disc list-inside text-gray-600">
                      {formData.highlights.filter(h => h.trim()).map((h, i) => (
                        <li key={`${h}-${i}`}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Gallery Preview */}
                {imagePreviews.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-2">Gallery ({formData.images.length} images uploaded)</h4>
                    <div className="flex gap-2 overflow-x-auto">
                      {imagePreviews.map((preview, index) => (
                        <img
                          key={preview}
                          src={preview}
                          alt={`Gallery ${index + 1}`}
                          className="w-20 h-20 object-cover rounded flex-shrink-0"
                        />
                      ))}
                    </div>
                    {formData.images.length === 0 && imagePreviews.length > 0 && (
                      <p className="text-red-500 text-sm mt-2">⚠️ Images are previewed but not uploaded to server. Please wait or try again.</p>
                    )}
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold"
                  >
                    ← Back to Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-6 border border-red-300 text-red-600 py-3 rounded-lg hover:bg-red-50 font-semibold"
                    data-testid="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                    data-testid="publish-service-btn"
                  >
                    {loading ? 'Publishing...' : '🚀 Publish Service'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      <Footer />

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => {
          setShowTermsModal(false);
          if (!user?.terms_accepted) {
            navigate('/dashboard');
          }
        }}
        onAccept={handleAcceptTerms}
        title="Accept Terms to Post Services"
      />
    </div>
  );
};

export default PostService;
